import { AppLink } from '@/shared/components/AppLink';

interface GiftCardItem {
  title: string;
  imageUrl: string;
  link: string;
  position?: number;
  lazyload?: string;
  shouldRefresh?: boolean;
}

interface GiftCardSectionProps {
  data: Record<string, unknown> & {
    title?: string;
    link?: string;
    bgImageUrl?: string;
    imageAlt?: string;
    blankImageUrl?: string;
    lazyload?: string;
    items?: GiftCardItem[];
    showTitle?: boolean;
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

export default function GiftCardSection({
  data,
}: GiftCardSectionProps) {
  const items = (data.items || []) as GiftCardItem[];
  const title = data.title;
  const showTitle = data.showTitle !== false;
  const bgImageUrl = data.bgImageUrl;
  const imageAlt = data.imageAlt || '';
  const lazyload = data.lazyload;

  if (!items || items.length === 0 || !bgImageUrl) {
    return null;
  }

  return (
    <section className="pt-8 md:py-0 md:pb-12 md:w-[90%] md:h-full md:flex md:flex-col md:justify-center md:items-center">
      {/* Title */}
      {showTitle && title && (
        <div className="container-fluid mb-6">
          <h2 className="mb-4 text-base md:text-[24px] font-normal text-center leading-normal md:leading-[30px]">
            <span className="font-primary text-[#9D7792] md:text-[#032C5E]">{title}</span>
          </h2>
        </div>
      )}

      {/* Gift Card Container */}
      <div className="relative w-full md:flex md:flex-col">
        {/* Background Image */}
        <img
          className="w-full h-auto object-contain block"
          src={bgImageUrl}
          alt={imageAlt}
          loading={lazyload ? 'lazy' : 'eager'}
          decoding="async"
        />

        {/* Items Overlay - positioned within the card area */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-[7%] flex w-[80%] items-center justify-center gap-2 md:gap-4 lg:gap-6">
          {items.map((item, index) => (
            <AppLink
              key={`${item.title}-${index}`}
              to={item.link}
              eventName="Home Page Gift Card Clicked"
              eventProperties={{
                'Card name': item.title,
              }}
              className="hover:opacity-90 transition-opacity inline-block"
              shouldRefresh={item.shouldRefresh}
            >
              <img
                className="block w-full h-auto object-contain"
                src={item.imageUrl}
                alt={item.title}
                width="97"
                height="118"
                loading={item.lazyload ? 'lazy' : 'eager'}
                decoding="async"
              />
            </AppLink>
          ))}
        </div>
      </div>
    </section>
  );
}

